import React, {
  type JSXElementConstructor,
  type ReactElement,
  type ReactNode,
  cloneElement,
  useId,
  useState,
} from 'react';
import type { Placement, Strategy } from '@floating-ui/react-dom';

import { classFilter } from '@shieldpay/theme-provider-ui/utilities';

import { usePositionedPopup } from '../../hooks/use-positioned-popup/use-positioned-popup';
import { Box } from '../box/box';
import { Text } from '../text/text';

import * as styles from './tooltip.css';

export type TooltipAriaDisabledProps<
  ChildProps extends React.PropsWithChildren<unknown>,
> = {
  children: ReactElement<JSXElementConstructor<ChildProps>>;
  tooltipPlacement?: Placement;
  tooltipText: ReactNode;
  tooltipStrategy?: Strategy;
  highlight?: boolean;
};

/**
 * Wraps an interactive element with a tooltip that is
 * visible when the element is hovered or focussed.
 */
export const TooltipAriaDisabled = <
  ChildProps extends React.PropsWithChildren<unknown>,
>({
  children,
  tooltipText,
  tooltipPlacement: placement = 'top',
  highlight = false,
  tooltipStrategy: strategy = 'absolute',
  // We need to spread the props here to allow cloneElement used
  // for the ActionMenu generated buttons to work correctly
  ...props
}: TooltipAriaDisabledProps<ChildProps>) => {
  const [open, setOpen] = useState(false);

  if (open !== highlight) {
    // If the highlight prop changes, we need to update the open state
    // this is used in the action menu to highlight the selected item
    setOpen(highlight);
  }

  const tooltipId = useId();

  const { setFloating, setReference, constrainStyles, floatingStyles } =
    usePositionedPopup<HTMLButtonElement, HTMLDialogElement>({
      open,
      placement,
      maxWidth: 'basePos12',
      strategy,
    });

  return (
    <>
      {cloneElement(children, {
        ...props,
        // we need to pass this down so child components can use it
        highlight,
        className: classFilter([
          styles.disabledButton,
          highlight && styles.highlightedByProp,
        ]),
        ref: setReference,
        'aria-describedby': tooltipId,
        'aria-disabled': 'true',
        onMouseEnter: () => setOpen(true),
        onMouseLeave: () => setOpen(false),
        onFocus: () => setOpen(true),
        onBlur: () => setOpen(false),
      } as unknown as ChildProps)}

      <Box
        Component="span"
        className={styles.tooltip}
        padding="base"
        ref={setFloating}
        style={floatingStyles}
        id={tooltipId}
      >
        <Box
          Component="span"
          background="neutral500"
          rounded
          style={constrainStyles}
        >
          <Text
            className={styles.text}
            color="neutral000"
            padding={{ x: 'baseNeg3', y: 'baseNeg5' }}
            role="tooltip"
            variant="caption150"
          >
            {tooltipText}
          </Text>
        </Box>
      </Box>
    </>
  );
};
